import type * as React from "react";

import { cx } from "../../lib/cx";
import s from "./Card.module.css";

/** `gradient` es el nombre viejo de `accent` y se acepta por compatibilidad. */
export type CardTone = "default" | "sunken" | "brand" | "elevated" | "inverse" | "accent" | "gradient";
export type CardPadding = "none" | "sm" | "md" | "lg";

export interface CardProps extends React.ComponentPropsWithoutRef<"div"> {
  /** Escala (`none | sm | md | lg` = 0 / 12 / 20 / 24 px) o, por compatibilidad, un número de esa misma escala. */
  padding?: CardPadding | 0 | 12 | 20 | 24;
  /** Sube la sombra al pasar el puntero y pone cursor de mano. La semántica la da `as`/`href`/`onClick`, no esto. */
  interactive?: boolean;
  /** default = blanca con borde gray-200; sunken = surface sin borde; brand = accent-50; elevated = sin borde, con
   *  sombra; inverse = bloque oscuro (ink); accent = banner violeta. Los dos últimos flipean los tokens de lo que
   *  tengan adentro. */
  tone?: CardTone;
  /** card = radio 8 (default); panel = radio 12, para las tarjetas de un tablero; xl = radio 16, la tarjeta grande
   *  que contiene un listado entero (la tabla de Propiedades). */
  radius?: "card" | "panel" | "xl";
  /** `section`, `article`, `a`, `li`… Por default un `div`. */
  as?: React.ElementType;
  href?: string;
}

const PADDING_POR_NUMERO: Record<0 | 12 | 20 | 24, CardPadding> = { 0: "none", 12: "sm", 20: "md", 24: "lg" };

/** Superficie contenedora: blanca, radio 8, borde gray-200. Máximo dos superficies por composición. */
export function Card({
  children,
  padding = "md",
  interactive = false,
  tone = "default",
  radius = "card",
  as: Tag = "div",
  className,
  ...rest
}: CardProps) {
  const escala = typeof padding === "number" ? PADDING_POR_NUMERO[padding] : padding;
  const tono = tone === "gradient" ? "accent" : tone;
  return (
    <Tag
      className={cx(s.card, tono === "inverse" && "klavia-dark", tono === "accent" && "klavia-accent", className)}
      data-tone={tono}
      data-padding={escala}
      data-radius={radius}
      data-interactive={interactive || undefined}
      {...rest}
    >
      {children}
    </Tag>
  );
}
